<template>
  <div>
    <b-row>
      <!-- Affichage de l'image actuelle ou nouvellement selectionnée -->
      <b-col cols="12" :md="buttonsPosition == 'right' ? 8 : 12">
        <div
          class="image-container"
          :class="{
            empty: !hasSelectedImage,
            'cursor-pointer': emptyContainerAction !== 'none',
          }"
          @click="handleEmptyContainerClick"
          @mouseenter="showOverlay = true"
          @mouseleave="showOverlay = false">
          <b-overlay :show="uploading">
            <div class="image-wrapper" style="position: relative">
              <b-img-lazy
                v-if="hasSelectedImage"
                :fluid="imageWidth == null"
                rounded
                :style="{
                  'object-fit': 'cover',
                  width: imageWidth,
                  height: imageHeight,
                }"
                :src="selectedImage.path"
                :alt="selectedImage.name" />
              <div v-if="hasSelectedImage" class="image-name-banner py-2">
                {{ selectedImage.name }}
              </div>
            </div>
            <transition name="fade">
              <div v-if="hasSelectedImage && showOverlay" class="image-overlay">
                <b-button @click="openFullscreenImage">Voir l'image</b-button>
              </div>
            </transition>

            <div
              v-if="!hasSelectedImage"
              class="placeholder"
              :style="{
                'object-fit': 'cover',
                width: imageWidth,
                height: imageHeight,
              }">
              <span>Aucune image sélectionnée</span>
            </div>
          </b-overlay>
        </div>
      </b-col>

      <!-- Boutons d'actions -->
      <b-col cols="12" :md="buttonsPosition == 'right' ? 4 : 12" class="mt-2 mt-md-0">
        <div class="button-container">
          <b-button
            v-if="!importButtonDisabled"
            class="w-100 text-nowrap"
            @click="openFileInput"
            :disabled="disabled || uploading">
            Importer depuis mon ordinateur
          </b-button>
          <b-button
            v-if="!chooseButtonDisabled"
            class="w-100 mt-2 text-nowrap"
            @click="openLibraryModal"
            :disabled="disabled || uploading">
            Choisir depuis la bibliothèque
          </b-button>
          <b-button
            v-if="hasSelectedImage && !resetButtonDisabled"
            class="w-100 mt-2 text-nowrap"
            variant="danger"
            @click="resetImage"
            :disabled="disabled || uploading">
            Réinitialiser
          </b-button>
        </div>
      </b-col>
    </b-row>

    <!-- Input pour l'import de fichier -->
    <input type="file" ref="fileInput" style="display: none" @change="onFileSelected" accept="image/*" />

    <!-- Modal pour la bibliothèque d'images -->
    <b-modal ref="libraryModal" title="Choisir une image" size="xl" centered hide-footer>
      <b-row class="mb-4">
        <b-col cols="12">
          <b-form-group id="fieldset-search-uploads-1" label="Rechercher" label-for="search-uploads">
            <b-form-input id="search-uploads" v-model="search" trim></b-form-input>
          </b-form-group>
        </b-col>
      </b-row>

      <!-- loading -->
      <div v-if="isLoading">
        <b-spinner />
      </div>

      <!-- empty container -->
      <div v-if="list.items.length === 0 && !isLoading">
        <p class="text-center">Aucun fichier trouvé</p>
      </div>

      <b-row align-v="stretch" v-else-if="list.items.length > 0">
        <b-col v-for="(file, index) in list.items" :key="index" cols="12" :sm="6" :md="4" :lg="2" class="mb-2">
          <div
            class="image-wrapper cursor-pointer"
            @click="selectLibraryImage(file)"
            v-b-tooltip.hover.bottom
            :title="file.name">
            <b-img-lazy
              thumbnail
              :style="{
                'object-fit': 'cover',
                padding: '0px',
                border: '0px',
              }"
              :src="file.path"
              :alt="file.name" />
          </div>
          <div class="gallery__image-name-banner font-weight-light font-size-xs">
            {{ file.name }}
          </div>
        </b-col>
      </b-row>

      <!-- pagination -->
      <div v-if="list.meta.total > 0" class="pagination-bar mb-3">
        <div class="pagination-total">{{ list.meta.total }} élément(s)</div>
        <div class="pagination-center">
          <b-pagination
            v-model="currentPage"
            :total-rows="list.meta.total"
            :per-page="perPage"
            align="center"
            @page-click="(event, page) => fetchFilesLibrary(page)"
            class="m-0"></b-pagination>
        </div>
        <div class="pagination-select">
          <b-form-select
            v-model="perPage"
            :options="[
              { value: 12, text: '12 par page' },
              { value: 24, text: '24 par page' },
              { value: 48, text: '48 par page' },
              { value: 96, text: '96 par page' },
            ]"
            size="sm"
            style="width: 120px"
            @change="handlePerPageChange"></b-form-select>
        </div>
      </div>
    </b-modal>

    <!-- Affichage de l'image en plein écran -->
    <transition name="fade">
      <div v-if="fullscreenImage" class="fullscreen-image" @click="closeFullscreenImage">
        <b-img :src="fullscreenImage.path" :alt="fullscreenImage.name" />
      </div>
    </transition>
  </div>
</template>

<script>
  import _isEqual from 'lodash/isEqual'
  import _debounce from 'lodash/debounce'
  import axios from 'axios'
  import fileService from '@/services/fileService'

  export default {
    name: 'ImageSelector',
    props: {
      imageHeight: {
        type: String,
        default: '200px',
      },
      imageWidth: {
        type: String,
        default: '100%',
      },
      buttonsPosition: {
        type: String,
        default: 'bottom',
        validator: (value) => ['bottom', 'right'].includes(value),
      },
      initialImage: {
        type: Object,
        default: null,
      },
      emptyContainerAction: {
        type: String,
        default: 'none',
        validator: (value) => ['none', 'import', 'choose'].includes(value),
      },
      uploadFileCategory: {
        type: String,
        default: 'default',
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      importButtonDisabled: {
        type: Boolean,
        default: false,
      },
      chooseButtonDisabled: {
        type: Boolean,
        default: false,
      },
      resetButtonDisabled: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        selectedImage: this.initialImage,
        showOverlay: false,
        fullscreenImage: null,
        uploading: false,
        search: '',
        debouncedSearch: null,
        isLoading: false,
        currentPage: 1,
        perPage: 12,
        list: {
          items: [],
          meta: {
            total: 0,
            page: this.currentPage,
            perPage: this.perPage,
            totalPages: 0,
            prevPage: null,
            nextPage: null,
          },
        },
      }
    },
    created() {
      this.debouncedSearch = _debounce(this.handleSearch, 500)
    },
    watch: {
      search(newValue) {
        this.debouncedSearch(newValue)
      },
      initialImage(newValue, oldValue) {
        if (_isEqual(newValue, oldValue)) {
          return
        }

        this.selectedImage = newValue
      },
    },
    computed: {
      jwt() {
        return localStorage.getItem('jwt')
      },
      hasSelectedImage() {
        return !!this.selectedImage && !!this.selectedImage.name
      },
      filteredFilesLibrary() {
        if (!this.search.trim()) {
          return this.list.items
        }

        return this.list.items.filter((file) => file.name.toLowerCase().includes(this.search.toLowerCase()))
      },
    },
    methods: {
      async handleSearch() {
        await this.fetchFilesLibrary(1)
      },
      async handlePerPageChange() {
        this.currentPage = 1
        await this.fetchFilesLibrary(1)
      },
      async fetchFilesLibrary(newPage) {
        this.isLoading = true
        this.list = await fileService.paginate({
          page: newPage,
          limit: this.perPage,
          type: 'image',
          search: this.search,
        })
        this.isLoading = false
      },
      handleEmptyContainerClick() {
        if (this.uploading) {
          return
        }

        if (this.disabled) {
          return
        }

        if (!this.hasSelectedImage) {
          if (this.emptyContainerAction === 'import') {
            this.openFileInput()
          } else if (this.emptyContainerAction === 'choose') {
            this.openLibraryModal()
          }
        }
      },
      openFileInput() {
        this.$refs.fileInput.click()
      },
      async openLibraryModal() {
        this.isLoading = true
        await this.fetchFilesLibrary(this.currentPage)
        this.isLoading = false
        this.$refs.libraryModal.show()
      },
      async onFileSelected(event) {
        const file = event.target.files[0]
        if (!file) {
          return
        }

        this.uploading = true
        try {
          const uploadedImageResponse = await this.uploadFile(file)
          const uploadedImage = uploadedImageResponse
          const uploadedImageData = {
            name: uploadedImage.name,
            path: uploadedImage.path,
            url: uploadedImage.url,
          }
          this.selectedImage = uploadedImageData
          this.$emit('image-uploaded', uploadedImageData)
          this.$emit('file-selected', uploadedImageData)
          this.$notify({
            type: 'success',
            title: 'Fichier téléchargé',
            text: 'Le fichier a été téléchargé avec succès',
          })
        } catch (e) {
          console.error(e)
          this.$notify({
            type: 'error',
            title: e || 'Erreur lors du téléchargement',
            text: e || 'Une erreur est survenue lors du téléchargement du fichier',
          })
        } finally {
          this.uploading = false
        }
      },
      async uploadFile(file) {
        if (!file) {
          return
        }

        const formData = new FormData()
        formData.append('category', this.uploadFileCategory)
        formData.append('file', file)

        const response = await axios.post('/api/files', formData, {
          withCredentials: true,
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: 'Bearer ' + this.jwt,
          },
        })

        return response.data
      },
      selectLibraryImage(image) {
        this.selectedImage = image
        this.$emit('file-selected', this.selectedImage)
        this.$refs.libraryModal.hide()
      },
      openFullscreenImage() {
        this.fullscreenImage = this.selectedImage
      },
      closeFullscreenImage() {
        this.fullscreenImage = null
      },
      resetImage() {
        this.selectedImage = null
        this.$emit('file-selected', null)
      },
    },
  }
</script>

<style scoped>
  .image-wrapper {
    position: relative;
    overflow: hidden;
    border-radius: 5px;
  }

  .button-container {
    display: flex;
    flex-direction: column;
  }

  .image-container.empty {
    background-color: #f8f9fa;
  }

  .placeholder {
    color: #6c757d;
    border: 2px dashed #6c757d;
    border-radius: 5px;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .image-name-banner {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: rgba(0, 0, 0, 0.7);
    color: white;
    padding: 5px 10px;
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .gallery__image-name-banner {
    padding: 5px 10px;
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .image-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .fullscreen-image {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.9);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
  }

  .fullscreen-image img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.2s;
  }
  .fade-enter,
  .fade-leave-to {
    opacity: 0;
  }

  .pagination-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    flex-wrap: nowrap;
  }
  .pagination-total {
    flex: 1;
    text-align: left;
    min-width: 120px;
  }
  .pagination-center {
    flex: 2;
    display: flex;
    justify-content: center;
    min-width: 200px;
  }
  .pagination-select {
    flex: 1;
    display: flex;
    justify-content: flex-end;
    min-width: 160px;
  }
  @media (max-width: 768px) {
    .pagination-bar {
      flex-direction: column;
      align-items: stretch;
      gap: 0.5rem;
      flex-wrap: wrap;
    }
    .pagination-total,
    .pagination-center,
    .pagination-select {
      text-align: center;
      justify-content: center !important;
    }
  }
</style>
